import React, { useCallback, useEffect, useState } from 'react';

import { Sheet } from '../../../mobile/structural/sheet';
import useSheet from '../../../mobile/structural/sheet/hooks/useSheet';
import { SheetId } from '../../../mobile/structural/sheet/sheetTypes';
import { holdFocusWithoutKeyboard } from '../../../mobile/boardsPage/editing/touchScreenFocusUtils';

import '../../../components/datetime/MobileDatePickerSheet.scss';

type TableDatePickerSheetProps = {
    showSessionId?: number;
    children: React.ReactNode;
    onFinishEditing: () => void;
};

const TableDatePickerSheet: React.FC<TableDatePickerSheetProps> = ({ showSessionId, children, onFinishEditing }) => {
    const sheetProps = useSheet(SheetId.TableDatePickerTool);
    const { closeSheet, isOpen, onOpenChange, openSheet } = sheetProps;

    const [isSheetOpen, setIsSheetOpen] = useState(false);

    // Open the sheet for every new date-time cell editing session, including the same cell.
    useEffect(() => {
        setIsSheetOpen(showSessionId !== undefined);
    }, [showSessionId]);

    // Synchronise the requested open state with the sheet transition state.
    useEffect(() => {
        const shouldOpenSheet = showSessionId !== undefined && isSheetOpen;
        if (isOpen === shouldOpenSheet) return;

        if (shouldOpenSheet) {
            openSheet();
            return;
        }

        closeSheet();
    }, [closeSheet, isOpen, isSheetOpen, openSheet, showSessionId]);

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            if (!isOpen) {
                setIsSheetOpen(false);
                onFinishEditing();
                return;
            }

            onOpenChange(true);
        },
        [onFinishEditing, onOpenChange],
    );

    return (
        <Sheet
            className="TableDatePickerSheet"
            {...sheetProps}
            onOpenChange={handleOpenChange}
            onCloseTransitionStart={holdFocusWithoutKeyboard}
        >
            <div data-table-util>
                <Sheet.Overlay />
            </div>
            <Sheet.Panel>
                <div data-table-util>
                    <Sheet.Content>{children}</Sheet.Content>
                </div>
            </Sheet.Panel>
        </Sheet>
    );
};

export default TableDatePickerSheet;
